import React from 'react';
import { useQueryClient } from 'react-query';
import {
  pipe,
  uniq,
  sortBy,
  identity
} from 'ramda';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SaveIcon from '@mui/icons-material/Save';
import { i18n } from 'i18n';
import AddIcon from '@mui/icons-material/Add';
import { makeLocalDate } from 'utils';
import {
  BulkImportMode,
  DufBulkImportGeometryEntityDto,
  DufBulkImportParseResponse,
  DufLayerRefDto,
  FileParameter,
  MINE_MODEL_KEY,
  MINE_MODEL_SUMMARY_KEY,
  RETICULATION_DATA_EXPORT_KEY,
  STOPE_BACKFILLS_KEY,
  useAddNodeBetweenNodesMutation,
  useBulkImportStopes,
  useBulkUpdateStopeGeometry,
  useParseDufForBulkImport
} from 'providers/api';
import { Model } from '../../hooks/useModelState';
import PCPipe from '../../MineModel/Pipes/PCPipe';
import { AlternativeNodeColour } from '../../MineModel/Nodes/PCNode';
import {
  AutoAssignSolidInput,
  AssignmentResult,
  SolidBoundingBox,
  computeProbePoint,
  findDuplicateStopeNames,
  normalizeStopeName,
  renameDuplicateSolids
} from './autoAssignUtils';
import {
  countAssignmentResults,
  countResolvedManualReviews,
  decisionsWithNodeApplied,
  firstUnresolvedManualReviewIndex,
  getBulkImportSolidsSceneState,
  updateBulkImportSolidsSceneState,
  subscribeToBulkImportSolidsSceneState,
  getCurrentSolid,
  getAttributeSolidName,
  getSolidName,
  getSolidStartDate,
  getSolidEndDate,
  getSolidVolumeAttribute,
  getNextInsertedNodeName,
  initClippingBoundsForSolid,
  setAccessType,
  setAutoAssignResults,
  setExistingStopeBehavior,
  setProcessingProgress,
  setRenameDuplicateNames,
  setSearchBox,
  setWizardPhase,
  ImportMode,
  SolidAction,
  SolidDecision
} from './bulkImportSolidsSceneState';
import {
  ManualReviewBlock,
  blockingReason,
  collectClaimedNodeIds,
  sourceNodeWarning
} from './manualReviewValidation';
import WizardStepAccessType from './WizardStepAccessType';
import WizardStepSearchParams from './WizardStepSearchParams';
import WizardStepProcessing from './WizardStepProcessing';
import WizardStepSummary from './WizardStepSummary';
import {
  BULK_IMPORT_GHOST_NODE_ID,
  createBulkImportGhostNode
} from './bulkImportGhost';

const ACTIVE_SOLID_HIGHLIGHT_COLOR = 0xffaa00;

interface ApiError {
  result?: { errors?: Record<string, string[]>; title?: string };
  message?: string;
}

const extractApiErrorMessage = (err: unknown, fallback: string): string => {
  const e = err as ApiError;
  return e?.result?.errors?.['']?.[0] || e?.result?.title || e?.message || fallback;
};

const noSupportedGeometryMessage = () => i18n._(
  'No supported stope geometry was found in this DUF. Reticulation exports do not contain stope solids; upload a DUF with mesh entities instead.',
);

interface BulkImportSolidsPanelProps {
  model: Model;
  onClose: () => void;
}

const blockMessage = (reason: ManualReviewBlock): string => ({
  no_action: i18n._('Choose what happens to this solid before saving it.'),
  no_node_selected: i18n._('Select the node in the model that becomes this stope.'),
  node_already_stope: i18n._('That node is already a stope. Pick a node that is not one yet.'),
  node_already_used: i18n._('Another solid in this import already becomes a stope on that node.'),
  no_source_node: i18n._('Select the node the new spool runs from, or add one on a spool. Without it the stope is imported connected to nothing.'),
  no_node_position: i18n._('Give the new node a position before saving.'),
  no_pipe_type: i18n._('Select a pipe type for the new spool.'),
  no_spool_selected: i18n._('Select the spool the new node is inserted into.'),
  no_existing_stope: i18n._('The existing stope to update could not be resolved.'),
}[reason]);

const findDefaultPipeType = (pipes: Record<string, PCPipe>): string | undefined => {
  const allPipes = Object.values(pipes);
  if (allPipes.length === 0) return undefined;

  const stopePipe = allPipes.find((p) => {
    const lowerName = p.name.toLowerCase();
    return lowerName.includes('hdpe') || lowerName.includes('stope');
  });
  return stopePipe?.pipeId ?? allPipes[0].pipeId;
};

const toSolidBoundingBox = (solid: DufBulkImportGeometryEntityDto): SolidBoundingBox | undefined => {
  const bb = solid.boundingBox;
  if (!bb) return undefined;
  return {
    minX: bb.minX ?? 0,
    maxX: bb.maxX ?? 0,
    minY: bb.minY ?? 0,
    maxY: bb.maxY ?? 0,
    minZ: bb.minZ ?? 0,
    maxZ: bb.maxZ ?? 0,
    centerX: bb.centerX,
    centerY: bb.centerY,
    centerZ: bb.centerZ,
  };
};

const toLocalDate = (dateStr: string | undefined) => {
  if (!dateStr) return undefined;
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return makeLocalDate(`${year}-${month}-${day}`);
};

const buildNewNodeForm = (decision: SolidDecision, nodeId: string, pipeId: string) => ({
  nodeId,
  point: {
    x: decision.newNodePosition?.x ?? 0,
    y: decision.newNodePosition?.y ?? 0,
    z: decision.newNodePosition?.z ?? 0,
  },
  pipeId,
  chokeHead: 0,
  isStope: true,
  isPit: false,
  level: '',
  area: '',
  stopeData: [{
    stopeName: decision.stopeName ?? '',
    volume: decision.volume ?? 0,
    startDate: toLocalDate(decision.startDate),
    endDate: toLocalDate(decision.endDate),
  }],
});

const mapDecisionToEntry = (decision: SolidDecision, existingNodeIds: Set<string>) => {
  const pipeId = decision.spoolPipeTypeId ?? '';
  const pipeArrays = pipeId ? [{ pipeId: { value: pipeId }, pipeHorizontalLength: 0, pipeVerticalLength: 0 }] : [];
  const isUpdate = decision.importMode === 'update_shapefile';
  const needsNewNode = !isUpdate && (decision.action === 'project_to_stope' || decision.action === 'insert_between_nodes');
  const newNodeId = needsNewNode ? getNextInsertedNodeName(existingNodeIds) : '';
  if (newNodeId) existingNodeIds.add(newNodeId);

  return {
    solidEntityGuid: decision.entityGuid,
    existingNodeId: !isUpdate && decision.action === 'mark_as_stope' ? decision.existingNodeId : undefined,
    newNodeForm: needsNewNode ? buildNewNodeForm(decision, newNodeId, pipeId) : undefined,
    insertBetweenNodes: !isUpdate && decision.action === 'insert_between_nodes' && decision.insertBetweenNodes
      ? {
        nodeAId: decision.insertBetweenNodes.nodeAId,
        nodeBId: decision.insertBetweenNodes.nodeBId,
      }
      : undefined,
    spoolFromNodeId: isUpdate ? undefined : decision.spoolFromNodeId,
    spoolPipeTypeId: isUpdate ? undefined : pipeId,
    stopeDataForm: {
      stopeId: isUpdate ? decision.existingStopeId : undefined,
      stopeName: decision.stopeName ?? '',
      volume: decision.volume ?? 0,
      startDate: toLocalDate(decision.startDate),
      endDate: toLocalDate(decision.endDate),
      pipeArrays,
    },
    mode: {
      skip: BulkImportMode.Skip,
      update_shapefile: BulkImportMode.UpdateShapefileOnly,
      full_import: BulkImportMode.FullImport,
    }[decision.importMode ?? 'full_import'],
  };
};

interface SpoolSegment {
  aId: string;
  aPos: { x: number; y: number; z: number };
  bId: string;
  bPos: { x: number; y: number; z: number };
}

const closestDistSqToSegment = (
  seg: SpoolSegment,
  p: { x: number; y: number; z: number },
): number => {
  const dx = seg.bPos.x - seg.aPos.x;
  const dy = seg.bPos.y - seg.aPos.y;
  const dz = seg.bPos.z - seg.aPos.z;
  const lenSq = dx * dx + dy * dy + dz * dz;
  const t = lenSq > 0
    ? Math.max(0, Math.min(1, ((p.x - seg.aPos.x) * dx + (p.y - seg.aPos.y) * dy + (p.z - seg.aPos.z) * dz) / lenSq))
    : 0;
  const cx = seg.aPos.x + t * dx;
  const cy = seg.aPos.y + t * dy;
  const cz = seg.aPos.z + t * dz;
  return (p.x - cx) ** 2 + (p.y - cy) ** 2 + (p.z - cz) ** 2;
};

const chainSpoolInsertions = (
  entries: ReturnType<typeof mapDecisionToEntry>[],
  nodes: Record<string, { coords: { x: number; y: number; z: number } }>,
): ReturnType<typeof mapDecisionToEntry>[] => {
  const spoolSegments = new Map<string, SpoolSegment[]>();
  const makeKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

  return entries.map((entry) => {
    if (!entry.insertBetweenNodes || !entry.newNodeForm) return entry;

    const { nodeAId, nodeBId } = entry.insertBetweenNodes;
    const key = makeKey(nodeAId, nodeBId);

    if (!spoolSegments.has(key)) {
      const nA = nodes[nodeAId];
      const nB = nodes[nodeBId];
      if (!nA || !nB) return entry;
      spoolSegments.set(key, [{
        aId: nodeAId,
        aPos: { x: nA.coords.x, y: nA.coords.y, z: nA.coords.z },
        bId: nodeBId,
        bPos: { x: nB.coords.x, y: nB.coords.y, z: nB.coords.z },
      }]);
    }

    const segments = spoolSegments.get(key)!;
    const newPos = entry.newNodeForm.point;

    let bestIdx = 0;
    let bestDist = Infinity;
    segments.forEach((seg, idx) => {
      const d = closestDistSqToSegment(seg, newPos);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = idx;
      }
    });

    const chosen = segments[bestIdx];
    const newNodeId = entry.newNodeForm.nodeId;
    segments.splice(
      bestIdx, 1,
      { aId: chosen.aId, aPos: chosen.aPos, bId: newNodeId, bPos: newPos },
      { aId: newNodeId, aPos: newPos, bId: chosen.bId, bPos: chosen.bPos },
    );

    return { ...entry, insertBetweenNodes: { nodeAId: chosen.aId, nodeBId: chosen.bId } };
  });
};

const BulkImportSolidsPanel = ({ model, onClose }: BulkImportSolidsPanelProps) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const [isConfirmingCancel, setIsConfirmingCancel] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const dufFileRef = React.useRef<File | null>(null);
  const parseMutation = useParseDufForBulkImport(model.project?.entityId ?? '');
  const importMutation = useBulkImportStopes(model.project?.entityId ?? '');
  const addNodeMutation = useAddNodeBetweenNodesMutation();
  const bulkGeometryMutation = useBulkUpdateStopeGeometry(model.project?.entityId ?? '');
  const queryClient = useQueryClient();

  const defaultPipeId = React.useMemo(() => findDefaultPipeType(model.pipes), [model.pipes]);

  const sceneState = getBulkImportSolidsSceneState();
  const currentSolid = getCurrentSolid();

  const modelRef = React.useRef(model);
  modelRef.current = model;

  const currentSolidGuid = currentSolid?.guid;
  const { wizardPhase } = sceneState;

  const markedNodeRef = React.useRef<{ id: string; prevColour: string } | null>(null);

  const restoreMarkedNode = React.useCallback(() => {
    const marker = markedNodeRef.current;
    if (!marker) return;
    const node = modelRef.current.nodes[marker.id];
    if (node) modelRef.current.setNodeColour(marker.id, marker.prevColour);
    markedNodeRef.current = null;
  }, []);

  const ghostNodeAction = currentSolid?.guid
    ? sceneState.decisions[currentSolid.guid]?.action
    : undefined;
  const { projectedNodePosition } = sceneState;

  const markedNodeId = ghostNodeAction === 'mark_as_stope'
    ? sceneState.selectedNodeId ?? null
    : null;

  const existingStopes = React.useMemo(() => {
    const allStopes = Object.values(model.stopes || {}).flatMap((arr) => arr ?? []);
    return allStopes.reduce<Record<string, string>>((acc, stope) => {
      const name = stope?.data?.stopeName;
      if (name) {
        acc[normalizeStopeName(name)] = stope.data.stopeId;
      }
      return acc;
    }, {});
  }, [model.stopes]);

  const entitiesOnSelectedLayer = React.useMemo<DufBulkImportGeometryEntityDto[]>(() => {
    const { solids, selectedLayerGuid } = sceneState;
    if (!selectedLayerGuid) return [];
    return solids.filter((e) => e.layerGuid === selectedLayerGuid);
  }, [sceneState.solids, sceneState.selectedLayerGuid]);

  const availableAttributes = React.useMemo<string[]>(() => {
    if (entitiesOnSelectedLayer.length === 0) return [];
    const allKeys = entitiesOnSelectedLayer.flatMap((e) => Object.keys(e.attributes ?? {}));
    return pipe(uniq, sortBy(identity))(allKeys) as string[];
  }, [entitiesOnSelectedLayer]);

  const duplicateNameSummary = React.useMemo(() => {
    const { selectedNameAttribute } = sceneState;
    if (!selectedNameAttribute || entitiesOnSelectedLayer.length === 0) return [];

    const selectedNames = entitiesOnSelectedLayer.map((entity) => {
      const attrValue = entity.attributes?.[selectedNameAttribute]?.trim();
      return attrValue || entity.guid || 'Unknown';
    });

    return findDuplicateStopeNames(selectedNames);
  }, [entitiesOnSelectedLayer, sceneState.selectedNameAttribute]);

  const hasDuplicateSelectedNames = duplicateNameSummary.length > 0;
  const duplicateNameExamples = duplicateNameSummary
    .slice(0, 5)
    .map((entry) => `${entry.name} (${entry.count})`)
    .join(', ');

  const layersWithGeometry = React.useMemo<DufLayerRefDto[]>(() => {
    const { layers, solids } = sceneState;
    if (!solids.length) return [];
    const geometryLayerGuids = new Set(
      solids.filter((e) => !!e.layerGuid)
        .map((e) => e.layerGuid as string),
    );
    return (layers ?? []).filter((layer) => layer.guid && geometryLayerGuids.has(layer.guid));
  }, [sceneState.layers, sceneState.solids]);

  const handleFileSelect = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    dufFileRef.current = file;
    updateBulkImportSolidsSceneState({
      solids: [],
      layers: [],
      imperialUnits: false,
      selectedLayerGuid: '',
      selectedNameAttribute: '',
      selectedStartDateAttribute: '',
      selectedEndDateAttribute: '',
      selectedVolumeAttribute: '',
      isParsing: true,
      error: null,
    });

    const fileParam: FileParameter = { data: file, fileName: file.name };

    parseMutation.mutate(
      { fileParam },
      {
        onSuccess: (response: DufBulkImportParseResponse) => {
          const geometryEntities = response.geometryEntities ?? [];
          updateBulkImportSolidsSceneState({
            solids: geometryEntities,
            layers: response.layers ?? [],
            imperialUnits: response.imperialUnits ?? false,
            isParsing: false,
            error: geometryEntities.length === 0 ? noSupportedGeometryMessage() : null,
          });
        },
        onError: (err: unknown) => {
          updateBulkImportSolidsSceneState({ isParsing: false, error: extractApiErrorMessage(err, 'Failed to parse DUF file') });
        },
      },
    );
  }, [parseMutation]);

  const handleLayerChange = (layerGuid: string) => {
    updateBulkImportSolidsSceneState({
      selectedLayerGuid: layerGuid,
      selectedNameAttribute: '',
      selectedStartDateAttribute: '',
      selectedEndDateAttribute: '',
      selectedVolumeAttribute: '',
    });
  };

  const handleNameAttributeChange = (attr: string) => {
    updateBulkImportSolidsSceneState({ selectedNameAttribute: attr });
  };

  const handleStartDateAttributeChange = (attr: string) => {
    updateBulkImportSolidsSceneState({ selectedStartDateAttribute: attr });
  };

  const handleEndDateAttributeChange = (attr: string) => {
    updateBulkImportSolidsSceneState({ selectedEndDateAttribute: attr });
  };

  const handleVolumeAttributeChange = (attr: string) => {
    updateBulkImportSolidsSceneState({ selectedVolumeAttribute: attr });
  };

  const handleStartReview = () => {
    const { selectedNameAttribute, selectedLayerGuid: layerGuid } = sceneState;
    if (!selectedNameAttribute || !layerGuid) return;

    const solidsOnLayer = sceneState.solids.filter((e) => e.layerGuid === layerGuid);
    const solidNameOverrides = sceneState.renameDuplicateNames
      ? renameDuplicateSolids(solidsOnLayer.flatMap((solid) => (solid.guid ? [{ guid: solid.guid, name: getAttributeSolidName(solid) }] : [])))
      : {};

    updateBulkImportSolidsSceneState({
      wizardPhase: 'access_type',
      solids: solidsOnLayer,
      solidNameOverrides,
      selectedNodeId: undefined,
      projectedNodePosition: null,
      decisions: {},
      autoAssignResults: [],
      manualReviewQueue: [],
      currentManualReviewIndex: 0,
      processingProgress: 0,
    });
  };

  const currentSolidName = currentSolid ? getSolidName(currentSolid) : '';
  const currentSolidNameKey = currentSolidName ? normalizeStopeName(currentSolidName) : '';
  const hasConflict = currentSolidNameKey ? !!existingStopes[currentSolidNameKey] : false;
  const currentDecision = currentSolid?.guid ? sceneState.decisions[currentSolid.guid] : undefined;

  const claimedNodeIds = React.useMemo(
    () => collectClaimedNodeIds(sceneState.decisions, currentSolidGuid),
    [sceneState.decisions, currentSolidGuid],
  );
  const manualReviewContext = {
    isStopeNode: (nodeId: string) => !!model.nodes[nodeId]?.isStope,
    claimedNodeIds,
  };
  const saveBlockedBy = blockingReason(currentDecision, manualReviewContext);
  const sourceWarning = sourceNodeWarning(currentDecision, manualReviewContext);

  const handleAction = (action: SolidAction) => {
    if (!currentSolid?.guid) return;

    const startDate = currentSolid ? getSolidStartDate(currentSolid) : undefined;
    const endDate = currentSolid ? getSolidEndDate(currentSolid) : undefined;

    // The same face auto-assign probes, so a bottom-access import does not put every node on the roof.
    const boundingBox = toSolidBoundingBox(currentSolid);
    const accessPoint = boundingBox ? computeProbePoint(boundingBox, sceneState.accessType) : undefined;

    const projPos = action === 'project_to_stope' ? (sceneState.projectedNodePosition ?? accessPoint ?? null) : null;

    updateBulkImportSolidsSceneState({
      projectedNodePosition: projPos,
      decisions: {
        ...sceneState.decisions,
        [currentSolid.guid]: {
          entityGuid: currentSolid.guid,
          action,
          existingNodeId: action === 'mark_as_stope' ? sceneState.selectedNodeId : undefined,
          newNodePosition: action === 'project_to_stope' ? (projPos ?? undefined) : undefined,
          spoolFromNodeId: action === 'project_to_stope' ? sceneState.selectedNodeId : undefined,
          spoolPipeTypeId: defaultPipeId,
          stopeName: currentSolidName,
          volume: getSolidVolumeAttribute(currentSolid),
          startDate,
          endDate,
          existingStopeId: hasConflict ? existingStopes[currentSolidNameKey] : undefined,
          importMode: hasConflict ? 'update_shapefile' : undefined,
          saved: false,
        },
      },
    });
  };

  const handleImportModeChange = (mode: ImportMode) => {
    if (!currentSolid?.guid) return;

    const baseDecision: SolidDecision = currentDecision ?? {
      entityGuid: currentSolid.guid,
      action: mode === 'update_shapefile' ? 'mark_as_stope' : 'pending',
      stopeName: currentSolidName,
      volume: getSolidVolumeAttribute(currentSolid),
      startDate: getSolidStartDate(currentSolid),
      endDate: getSolidEndDate(currentSolid),
      spoolPipeTypeId: defaultPipeId,
      saved: false,
    };

    updateBulkImportSolidsSceneState({
      decisions: {
        ...sceneState.decisions,
        [currentSolid.guid]: {
          ...baseDecision,
          importMode: mode,
          existingStopeId: mode === 'update_shapefile' ? existingStopes[currentSolidNameKey] : baseDecision.existingStopeId,
        },
      },
    });
  };

  const handlePipeTypeChange = (pipeId: string) => {
    if (!currentSolid?.guid || !currentDecision) return;
    updateBulkImportSolidsSceneState({
      decisions: {
        ...sceneState.decisions,
        [currentSolid.guid]: { ...currentDecision, spoolPipeTypeId: pipeId },
      },
    });
  };

  const handleNodePositionField = (axis: 'x' | 'y' | 'z', value: string) => {
    if (!currentSolid?.guid || !currentDecision) return;
    const numVal = parseFloat(value);
    if (Number.isNaN(numVal)) return;
    const currentPos = currentDecision.newNodePosition ?? { x: 0, y: 0, z: 0 };
    const newPos = { ...currentPos, [axis]: numVal };
    updateBulkImportSolidsSceneState({
      projectedNodePosition: newPos,
      decisions: {
        ...sceneState.decisions,
        [currentSolid.guid]: { ...currentDecision, newNodePosition: newPos },
      },
    });
  };

  const moveToNextSolid = () => {
    const nextReviewIndex = sceneState.currentManualReviewIndex + 1;
    const finished = nextReviewIndex >= sceneState.manualReviewQueue.length;

    updateBulkImportSolidsSceneState({
      currentManualReviewIndex: finished ? sceneState.currentManualReviewIndex : nextReviewIndex,
      selectedNodeId: undefined,
      projectedNodePosition: null,
      spoolClickInfo: null,
      isSelectingSpoolForInsert: false,
      ...(finished ? { wizardPhase: 'summary' as const } : {}),
    });
    // The projection line follows the selection, which would otherwise show a spool for the next solid
    // that its decision is not going to create.
    model.setSelectedNode(undefined);
    model.setClippingBounds(null);
  };

  const handleSaveAndNext = () => {
    if (!currentSolid?.guid) return;

    const decision = sceneState.decisions[currentSolid.guid];
    if (!decision || saveBlockedBy) return;

    updateBulkImportSolidsSceneState({
      decisions: { ...sceneState.decisions, [currentSolid.guid]: { ...decision, saved: true } },
    });
    moveToNextSolid();
  };

  const handleSkip = () => {
    if (!currentSolid?.guid) return;

    const skipDecision: SolidDecision = {
      entityGuid: currentSolid.guid,
      action: 'pending',
      stopeName: currentSolidName,
      importMode: 'skip',
      saved: true,
    };

    updateBulkImportSolidsSceneState({
      decisions: { ...sceneState.decisions, [currentSolid.guid]: skipDecision },
    });

    moveToNextSolid();
  };

  const handleInsertNode = () => {
    const { spoolClickInfo } = sceneState;
    if (!spoolClickInfo) return;

    const nodeA = model.findNodeById(spoolClickInfo.nodeAId);
    const nodeB = model.findNodeById(spoolClickInfo.nodeBId);
    if (!nodeA || !nodeB) return;

    const spool = model.spools[spoolClickInfo.spoolId];
    if (!spool) return;

    const spoolPipeId = spool.pipe?.pipeId ?? defaultPipeId ?? '';
    const newNodeId = getNextInsertedNodeName(new Set(Object.keys(model.nodes)));

    updateBulkImportSolidsSceneState({ isInsertingNode: true });

    // Splitting the spool is one command: the server drops the original edge and adds the two halves
    // in a single save of the per-mine reticulation document.
    addNodeMutation.mutate(
      {
        projectId: model.project?.entityId ?? '',
        data: {
          node: {
            nodeId: newNodeId,
            point: spoolClickInfo.clickPoint,
            pipeId: spoolPipeId,
            chokeHead: 0,
            isStope: false,
            isPit: false,
            isPlantNode: false,
          },
          nodeAId: nodeA.id,
          nodeBId: nodeB.id,
          spoolPipeId,
        },
      },
      {
        onSuccess: () => {
          // Without this the inserted node is selected and highlighted, but nothing references it.
          updateBulkImportSolidsSceneState({
            isInsertingNode: false,
            spoolClickInfo: null,
            selectedNodeId: newNodeId,
            ...decisionsWithNodeApplied(newNodeId),
          });
          model.setSelectedNode(model.findNodeById(newNodeId) ?? undefined);
        },
        onError: (err: unknown) => {
          updateBulkImportSolidsSceneState({ isInsertingNode: false, error: extractApiErrorMessage(err, 'Failed to insert node') });
        },
      },
    );
  };

  const handleCancelImport = () => {
    model.setSelectedNode(undefined);
    onClose();
  };

  React.useEffect(() => {
    const unsubscribe = subscribeToBulkImportSolidsSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    const m = modelRef.current;
    m.clearImportedSolids();
    if (wizardPhase !== 'manual_review' || !currentSolid?.objData || !currentSolid?.guid) {
      m.setClippingBounds(null);
      updateBulkImportSolidsSceneState({ computedMeshVolume: null });
      return;
    }
    const bb = currentSolid.boundingBox;
    m.addImportedSolid({
      guid: currentSolid.guid,
      objData: currentSolid.objData,
      name: getSolidName(currentSolid),
      highlightColor: ACTIVE_SOLID_HIGHLIGHT_COLOR,
      boundingBox: bb ? {
        minX: bb.minX ?? 0,
        maxX: bb.maxX ?? 0,
        minY: bb.minY ?? 0,
        maxY: bb.maxY ?? 0,
        minZ: bb.minZ ?? 0,
        maxZ: bb.maxZ ?? 0,
        centerX: bb.centerX ?? (((bb.minX ?? 0) + (bb.maxX ?? 0)) / 2),
        centerY: bb.centerY ?? (((bb.minY ?? 0) + (bb.maxY ?? 0)) / 2),
        centerZ: bb.centerZ ?? (((bb.minZ ?? 0) + (bb.maxZ ?? 0)) / 2),
        volume: bb.volume,
      } : undefined,
    });
    if (!m.clippingBounds) {
      const bounds = initClippingBoundsForSolid(currentSolid);
      m.setClippingBounds(bounds);
    }
    updateBulkImportSolidsSceneState({
      computedMeshVolume: bb?.volume ?? null,
    });
  }, [wizardPhase, currentSolidGuid]);

  React.useEffect(() => () => {
    const m = modelRef.current;
    m.clearImportedSolids();
    m.setClippingBounds(null);
    m.setShowClippingBounds(false);
    if (m.nodes[BULK_IMPORT_GHOST_NODE_ID]) {
      m.removeNodes([m.nodes[BULK_IMPORT_GHOST_NODE_ID]]);
    }
    restoreMarkedNode();
  }, [restoreMarkedNode]);

  React.useEffect(() => {
    const m = modelRef.current;
    const shouldHaveGhost = ghostNodeAction === 'project_to_stope' && !!projectedNodePosition;
    const existingGhost = m.nodes[BULK_IMPORT_GHOST_NODE_ID];

    if (!shouldHaveGhost) {
      if (existingGhost) m.removeNodes([existingGhost]);
      return;
    }

    if (!existingGhost) {
      const pipeId = defaultPipeId ?? Object.values(m.pipes)[0]?.pipeId ?? '';
      m.addNode(createBulkImportGhostNode(projectedNodePosition!, pipeId));
      return;
    }

    const c = existingGhost.coords;
    if (c.x !== projectedNodePosition!.x || c.y !== projectedNodePosition!.y || c.z !== projectedNodePosition!.z) {
      m.setNodeProperties(BULK_IMPORT_GHOST_NODE_ID, {
        coords: projectedNodePosition as any,
      });
    }
  }, [ghostNodeAction, projectedNodePosition, defaultPipeId]);

  React.useEffect(() => {
    const m = modelRef.current;
    const marker = markedNodeRef.current;

    if (marker && marker.id !== markedNodeId) {
      restoreMarkedNode();
    }

    if (markedNodeId && (!marker || marker.id !== markedNodeId)) {
      const node = m.nodes[markedNodeId];
      if (node) {
        markedNodeRef.current = { id: markedNodeId, prevColour: node.nodeColour };
        m.setNodeColour(markedNodeId, AlternativeNodeColour.DELETE);
      }
    }
  }, [markedNodeId, restoreMarkedNode]);

  const autoAssignInputs = React.useMemo<AutoAssignSolidInput[]>(() => (
    sceneState.solids.reduce<AutoAssignSolidInput[]>((acc, solid) => {
      const boundingBox = toSolidBoundingBox(solid);
      if (!solid.guid || !boundingBox) return acc;
      return [...acc, { guid: solid.guid, name: getSolidName(solid), boundingBox }];
    }, [])
  ), [sceneState.solids, sceneState.selectedNameAttribute, sceneState.solidNameOverrides]);

  const nodesList = React.useMemo(() => Object.values(model.nodes ?? {}), [model.nodes]);
  const spoolsList = React.useMemo(() => Object.values(model.spools ?? {}), [model.spools]);

  // Splitting a spool must keep that spool's own pipe, not the project default — the two halves
  // inherit it. The pipe is a property of the spool, so there is nothing else to resolve.
  const resolveSpoolPipeTypeId = (result: AssignmentResult | undefined): string | undefined => {
    if (result?.status !== 'insert_between_nodes' || !result.nodeAId || !result.nodeBId) return defaultPipeId;
    return model.findSpoolByPair(result.nodeAId, result.nodeBId)?.pipe?.pipeId ?? defaultPipeId;
  };

  const handleAutoAssignComplete = (results: AssignmentResult[]) => {
    const solidsByGuid = new Map(sceneState.solids.map((s) => [s.guid ?? '', s]));
    const resultsByGuid = new Map(results.map((r) => [r.solidGuid, r]));
    setAutoAssignResults(results, (solidGuid) => {
      const solid = solidsByGuid.get(solidGuid);
      if (!solid) return {};
      return {
        stopeName: getSolidName(solid),
        volume: getSolidVolumeAttribute(solid),
        startDate: getSolidStartDate(solid),
        endDate: getSolidEndDate(solid),
        spoolPipeTypeId: resolveSpoolPipeTypeId(resultsByGuid.get(solidGuid)),
      };
    });
    setWizardPhase('summary');
  };

  const handleSaveAll = async () => {
    const m = modelRef.current;
    const existingIds = new Set(Object.keys(m.nodes));
    const submittedSolids: { solid: DufBulkImportGeometryEntityDto; entryIndex: number }[] = [];
    const rawEntries: ReturnType<typeof mapDecisionToEntry>[] = [];
    sceneState.solids.forEach((solid) => {
      if (!solid.guid) return;
      const decision = sceneState.decisions[solid.guid];
      if (!decision) return;
      if (decision.importMode === 'skip' || decision.action === 'pending') return;
      const entry = mapDecisionToEntry(decision, existingIds);
      submittedSolids.push({ solid, entryIndex: rawEntries.length });
      rawEntries.push(entry);
    });

    const entries = chainSpoolInsertions(rawEntries, m.nodes);

    if (entries.length === 0) {
      setWizardPhase('done');
      return;
    }

    updateBulkImportSolidsSceneState({ isSaving: true, error: null });

    try {
      const raw = await importMutation.mutateAsync({ form: { entries } });
      const createdStopeIds = raw.createdStopeIds ?? [];
      const geometryEntries = submittedSolids
        .filter(({ solid, entryIndex }) => createdStopeIds[entryIndex] && solid.objData)
        .map(({ solid, entryIndex }) => ({
          stopeId: createdStopeIds[entryIndex],
          objContent: solid.objData as string,
          fileName: `${solid.guid ?? 'geometry'}.obj`,
        }));

      if (geometryEntries.length > 0) {
        await bulkGeometryMutation.mutateAsync({ form: { entries: geometryEntries } });
      }

      [MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, MINE_MODEL_SUMMARY_KEY, STOPE_BACKFILLS_KEY]
        .forEach((key) => queryClient.invalidateQueries(key, { exact: false }));
      updateBulkImportSolidsSceneState({ isSaving: false });
      setWizardPhase('done');
    } catch (err) {
      updateBulkImportSolidsSceneState({ isSaving: false, error: extractApiErrorMessage(err, 'Failed to save import') });
    }
  };

  if (wizardPhase === 'upload') {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">{i18n._('Bulk Import Solids')}</Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".duf"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <Button
          variant="outlined"
          startIcon={sceneState.isParsing ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={sceneState.isParsing}
          color="secondary"
        >
          {sceneState.isParsing ? i18n._('Parsing...') : i18n._('Upload DUF File')}
        </Button>

        {sceneState.error && <Alert severity="error">{sceneState.error}</Alert>}

        {sceneState.solids.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary">
              {i18n._('{count} geometry entities found', { count: sceneState.solids.length })}
            </Typography>

            <FormControl fullWidth size="small" color="secondary">
              <InputLabel id="bulk-import-layer-label">{i18n._('Layer')}</InputLabel>
              <Select
                labelId="bulk-import-layer-label"
                value={sceneState.selectedLayerGuid}
                label={i18n._('Layer')}
                onChange={(e) => handleLayerChange(e.target.value)}
              >
                {layersWithGeometry.map((layer) => (
                  <MenuItem key={layer.guid} value={layer.guid}>
                    {layer.name || layer.guid}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {sceneState.selectedLayerGuid && availableAttributes.length > 0 && (
              <>
                <FormControl fullWidth size="small" color="secondary">
                  <InputLabel id="bulk-import-name-attribute-label">{i18n._('Stope Name Attribute')}</InputLabel>
                  <Select
                    labelId="bulk-import-name-attribute-label"
                    value={sceneState.selectedNameAttribute}
                    label={i18n._('Stope Name Attribute')}
                    onChange={(e) => handleNameAttributeChange(e.target.value)}
                  >
                    {availableAttributes.map((attr) => (
                      <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" color="secondary">
                  <InputLabel id="bulk-import-volume-attribute-label">{i18n._('Volume Attribute (optional)')}</InputLabel>
                  <Select
                    labelId="bulk-import-volume-attribute-label"
                    value={sceneState.selectedVolumeAttribute}
                    label={i18n._('Volume Attribute (optional)')}
                    onChange={(e) => handleVolumeAttributeChange(e.target.value)}
                  >
                    <MenuItem value="">{i18n._('None')}</MenuItem>
                    {availableAttributes.map((attr) => (
                      <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {sceneState.imperialUnits && (
                  <Alert severity="info">
                    {i18n._('This DUF is in imperial units. Geometry and volumes are converted to metric on import.')}
                  </Alert>
                )}

                <FormControl fullWidth size="small" color="secondary">
                  <InputLabel id="bulk-import-start-date-attribute-label">{i18n._('Start Date Attribute (optional)')}</InputLabel>
                  <Select
                    labelId="bulk-import-start-date-attribute-label"
                    value={sceneState.selectedStartDateAttribute}
                    label={i18n._('Start Date Attribute (optional)')}
                    onChange={(e) => handleStartDateAttributeChange(e.target.value)}
                  >
                    <MenuItem value="">{i18n._('None')}</MenuItem>
                    {availableAttributes.map((attr) => (
                      <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" color="secondary">
                  <InputLabel id="bulk-import-end-date-attribute-label">{i18n._('End Date Attribute (optional)')}</InputLabel>
                  <Select
                    labelId="bulk-import-end-date-attribute-label"
                    value={sceneState.selectedEndDateAttribute}
                    label={i18n._('End Date Attribute (optional)')}
                    onChange={(e) => handleEndDateAttributeChange(e.target.value)}
                  >
                    <MenuItem value="">{i18n._('None')}</MenuItem>
                    {availableAttributes.map((attr) => (
                      <MenuItem key={attr} value={attr}>{attr}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {sceneState.selectedNameAttribute && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {i18n._('{count} solids on selected layer', { count: entitiesOnSelectedLayer.length })}
                </Typography>
                {hasDuplicateSelectedNames && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    {i18n._('Selected stope name attribute contains duplicate values. You can still start review, or pick a more unique attribute.')}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {i18n._('{nameCount} duplicate names affect {solidCount} solids. Examples: {examples}', {
                        nameCount: duplicateNameSummary.length,
                        solidCount: duplicateNameSummary.reduce((sum, entry) => sum + entry.count, 0),
                        examples: duplicateNameExamples,
                      })}
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={(
                        <Checkbox
                          size="small"
                          color="secondary"
                          checked={sceneState.renameDuplicateNames}
                          onChange={(e) => setRenameDuplicateNames(e.target.checked)}
                        />
                      )}
                      label={<Typography variant="body2">{i18n._('Rename duplicates by adding -1, -2, etc. to the end')}</Typography>}
                    />
                  </Alert>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleStartReview}
                  disabled={entitiesOnSelectedLayer.length === 0}
                >
                  {i18n._('Start Review')}
                </Button>
              </>
            )}
          </>
        )}
      </Box>
    );
  }

  if (wizardPhase === 'access_type') {
    return (
      <WizardStepAccessType
        accessType={sceneState.accessType}
        onSelect={(accessType) => setAccessType(accessType)}
        onBack={() => setWizardPhase('upload')}
        onConfirm={() => setWizardPhase('search_params')}
      />
    );
  }

  if (wizardPhase === 'search_params') {
    return (
      <WizardStepSearchParams
        accessType={sceneState.accessType}
        searchBox={sceneState.searchBox}
        existingStopeBehavior={sceneState.existingStopeBehavior}
        onChangeSearchBox={(box) => setSearchBox(box)}
        onChangeExistingStopeBehavior={(behavior) => setExistingStopeBehavior(behavior)}
        onBack={() => setWizardPhase('access_type')}
        onConfirm={() => setWizardPhase('processing')}
      />
    );
  }

  if (wizardPhase === 'processing') {
    return (
      <WizardStepProcessing
        solids={autoAssignInputs}
        nodes={nodesList}
        spools={spoolsList}
        accessType={sceneState.accessType}
        searchBox={sceneState.searchBox}
        existingStopesByName={existingStopes}
        existingStopeBehavior={sceneState.existingStopeBehavior}
        onProgress={(n) => setProcessingProgress(n)}
        onComplete={handleAutoAssignComplete}
      />
    );
  }

  if (wizardPhase === 'summary') {
    const counts = countAssignmentResults(sceneState.autoAssignResults);
    const savableCount = Object.values(sceneState.decisions)
      .filter((d) => d.importMode !== 'skip' && d.action !== 'pending').length;
    return (
      <WizardStepSummary
        counts={counts}
        savableCount={savableCount}
        manualReviewResolvedCount={countResolvedManualReviews()}
        isSaving={sceneState.isSaving}
        onBack={() => setWizardPhase('search_params')}
        onProceedToManualReview={() => updateBulkImportSolidsSceneState({
          wizardPhase: 'manual_review',
          currentManualReviewIndex: firstUnresolvedManualReviewIndex(),
        })}
        onSaveAndFinish={handleSaveAll}
      />
    );
  }

  if (wizardPhase === 'done') {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6">{i18n._('Import Complete')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {i18n._('The bulk import has been saved.')}
        </Typography>
        {sceneState.error && <Alert severity="error">{sceneState.error}</Alert>}
        <Button variant="contained" color="secondary" onClick={onClose} fullWidth>
          {i18n._('Close')}
        </Button>
      </Box>
    );
  }

  if (!currentSolid) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{i18n._('All solids reviewed')}</Typography>
        <Button variant="contained" color="secondary" onClick={handleSaveAll} sx={{ mt: 2 }} disabled={sceneState.isSaving}>
          {i18n._('Save All')}
        </Button>
      </Box>
    );
  }

  const allPipes = Object.values(model.pipes);
  const selectedNode = sceneState.selectedNodeId ? model.findNodeById(sceneState.selectedNodeId) : undefined;
  const volumeAttributeValue = currentSolid ? getSolidVolumeAttribute(currentSolid) : undefined;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" noWrap>
        {currentSolidName}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Chip
          label={`${sceneState.currentManualReviewIndex + 1} / ${sceneState.manualReviewQueue.length}`}
          size="small"
          color="primary"
        />
        {sceneState.computedMeshVolume !== null && (
          <Typography variant="body2" color="text.secondary">
            {i18n._('Mesh: {vol} m³', { vol: sceneState.computedMeshVolume.toFixed(1) })}
          </Typography>
        )}
      </Box>

      {sceneState.computedMeshVolume !== null && volumeAttributeValue !== undefined && (() => {
        const diff = Math.abs(sceneState.computedMeshVolume - volumeAttributeValue);
        const pct = volumeAttributeValue > 0 ? (diff / volumeAttributeValue) * 100 : 0;
        return pct > 1 ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {i18n._('Volume mismatch: attribute says {attr} m³ but mesh is {mesh} m³ ({pct}% difference)', {
              attr: volumeAttributeValue.toFixed(1),
              mesh: sceneState.computedMeshVolume.toFixed(1),
              pct: pct.toFixed(1),
            })}
          </Alert>
        ) : null;
      })()}

      <Button
        variant={model.showClippingBounds ? 'contained' : 'outlined'}
        size="small"
        color="secondary"
        fullWidth
        onClick={() => model.setShowClippingBounds(!model.showClippingBounds)}
      >
        {i18n._('Show Clipping Bounds')}
      </Button>

      {hasConflict && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {i18n._('Stope "{name}" already exists', { name: currentSolidName })}
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <ButtonGroup size="small" variant="outlined" color="secondary">
              <Button
                onClick={() => handleImportModeChange('update_shapefile')}
                variant={currentDecision?.importMode === 'update_shapefile' ? 'contained' : 'outlined'}
                color="secondary"
              >
                {i18n._('Update Shape')}
              </Button>
            </ButtonGroup>
          </Box>
        </Alert>
      )}

      {!hasConflict && currentDecision?.importMode !== 'skip' && (
        <>
          <Button
            variant={sceneState.isSelectingSpoolForInsert ? 'contained' : 'outlined'}
            size="small"
            color="secondary"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => updateBulkImportSolidsSceneState({
              isSelectingSpoolForInsert: !sceneState.isSelectingSpoolForInsert,
              spoolClickInfo: null,
            })}
          >
            {sceneState.isSelectingSpoolForInsert ? i18n._('Click a spool...') : i18n._('Add node at spool')}
          </Button>

          {sceneState.spoolClickInfo && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="body2">
                {i18n._('Clicked spool between {a} and {b}', {
                  a: sceneState.spoolClickInfo.nodeAId,
                  b: sceneState.spoolClickInfo.nodeBId,
                })}
              </Typography>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                startIcon={sceneState.isInsertingNode ? <CircularProgress size={14} /> : <AddIcon />}
                onClick={handleInsertNode}
                disabled={sceneState.isInsertingNode}
                sx={{ mt: 0.5 }}
              >
                {i18n._('Insert Node Here')}
              </Button>
            </Alert>
          )}

          <Divider />

          {selectedNode ? (
            <Typography variant="body2" color="text.secondary">
              {i18n._('Selected: {id}', { id: selectedNode.id })}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {i18n._('Click a node in the model, or add one on a spool, to choose what this solid attaches to.')}
            </Typography>
          )}

          <Button
            variant={currentDecision?.action === 'mark_as_stope' ? 'contained' : 'outlined'}
            size="small"
            color="secondary"
            fullWidth
            disabled={!selectedNode}
            onClick={() => handleAction('mark_as_stope')}
          >
            {i18n._('Make node Stope')}
          </Button>
          <Button
            variant={currentDecision?.action === 'project_to_stope' ? 'contained' : 'outlined'}
            size="small"
            color="secondary"
            fullWidth
            disabled={!selectedNode}
            onClick={() => handleAction('project_to_stope')}
          >
            {i18n._('Project node to Stope')}
          </Button>

          {currentDecision?.action === 'project_to_stope' && (
            <>
              <Divider />
              <Typography variant="subtitle2">{i18n._('Projected Node Position')}</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="X"
                  size="small"
                  color="secondary"
                  type="number"
                  value={currentDecision.newNodePosition?.x ?? 0}
                  onChange={(e) => handleNodePositionField('x', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Y"
                  size="small"
                  color="secondary"
                  type="number"
                  value={currentDecision.newNodePosition?.y ?? 0}
                  onChange={(e) => handleNodePositionField('y', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Z"
                  size="small"
                  color="secondary"
                  type="number"
                  value={currentDecision.newNodePosition?.z ?? 0}
                  onChange={(e) => handleNodePositionField('z', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>

              <FormControl fullWidth size="small" color="secondary">
                <InputLabel id="bulk-import-pipe-type-label">{i18n._('Pipe Type')}</InputLabel>
                <Select
                  labelId="bulk-import-pipe-type-label"
                  value={currentDecision.spoolPipeTypeId ?? ''}
                  label={i18n._('Pipe Type')}
                  onChange={(e) => handlePipeTypeChange(e.target.value)}
                >
                  {allPipes.map((p) => (
                    <MenuItem key={p.pipeId} value={p.pipeId}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {getSolidStartDate(currentSolid) && (
            <Typography variant="body2" color="text.secondary">
              {i18n._('Start Date: {date}', { date: getSolidStartDate(currentSolid) })}
            </Typography>
          )}

          {getSolidEndDate(currentSolid) && (
            <Typography variant="body2" color="text.secondary">
              {i18n._('End Date: {date}', { date: getSolidEndDate(currentSolid) })}
            </Typography>
          )}
        </>
      )}

      <Divider />

      {sourceWarning && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {i18n._('The selected node is already a stope, so fill would run through it to reach this one.')}
        </Alert>
      )}

      {saveBlockedBy && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          {blockMessage(saveBlockedBy)}
        </Alert>
      )}

      <Button
        variant="contained"
        size="small"
        color="secondary"
        fullWidth
        startIcon={sceneState.isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
        onClick={handleSaveAndNext}
        disabled={sceneState.isSaving || !!saveBlockedBy}
      >
        {sceneState.currentManualReviewIndex + 1 >= sceneState.manualReviewQueue.length
          ? i18n._('Save & Finish')
          : i18n._('Save & Next')}
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="secondary"
        fullWidth
        startIcon={<SkipNextIcon />}
        onClick={handleSkip}
        disabled={sceneState.isSaving}
      >
        {i18n._('Skip')}
      </Button>
      {isConfirmingCancel ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {i18n._('Cancel this import? Nothing from this file has been saved yet. Nodes you have inserted stay in the model.')}
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" color="error" onClick={handleCancelImport}>
              {i18n._('Cancel import')}
            </Button>
            <Button variant="outlined" size="small" color="secondary" onClick={() => setIsConfirmingCancel(false)}>
              {i18n._('Keep reviewing')}
            </Button>
          </Box>
        </Alert>
      ) : (
        <Button
          variant="text"
          size="small"
          color="error"
          fullWidth
          onClick={() => setIsConfirmingCancel(true)}
          disabled={sceneState.isSaving}
        >
          {i18n._('Cancel')}
        </Button>
      )}

      {sceneState.error && <Alert severity="error">{sceneState.error}</Alert>}
    </Box>
  );
};

export default BulkImportSolidsPanel;
